import { useEffect } from 'react'

import {
  useGetUserInfoQuery,
  CustomerUserInfoDataFragment,
  CustomerInfoFragment,
  UserType,
  CustomerUserPermissions
} from 'graph/generated/payments/graphql-types'
import Cookies from 'js-cookie'
import { usePostHog } from 'posthog-js/react'
import { AppCookie } from 'utils/cookies'
import { actionTracking } from 'utils/posthog'

import { useCompanyAndInvoiceContext } from '../providers/CompanyAndInvoiceProvider'

const guestUser: CustomerUserInfoDataFragment = {
  __typename: 'CustomerUserInfo',
  id: '00000000-0000-0000-0000-000000000000',
  role: 'guest',
  email: '',
  createdAt: '',
  permissions: [
    CustomerUserPermissions.BankingNew,
    CustomerUserPermissions.CardManage
  ]
}

/**
 * Hook to extract active user account
 * information based on X-Customer-Id cookie
 */
const useActiveUserInfo = (): {
  loading: boolean
  user: CustomerUserInfoDataFragment
  customer: CustomerInfoFragment
  userType: UserType
} => {
  const posthog = usePostHog()
  const customerId = Cookies.get(AppCookie.CustomerId)
  const isLoggedAsGuest = Cookies.get(AppCookie.LoggedAsGuest)

  const { data, loading } = useGetUserInfoQuery()
  const { invoice } = useCompanyAndInvoiceContext()
  const user = data?.GetUserInfo?.users?.find(
    u => u.customer?.id === customerId
  )

  useEffect(() => {
    if (user?.email) {
      Cookies.remove(AppCookie.LoggedAsGuest)
    }

    const partnerId = user?.customer?.partner?.id || invoice?.partner?.id

    if (!posthog || !partnerId) return

    // Check if user is identified and has partnerId property
    const userProperties = posthog.get_property('partnerId')
    const needsReidentification = posthog._isIdentified() && !userProperties

    if (
      posthog._isIdentified() &&
      posthog.sessionRecordingStarted() &&
      !needsReidentification
    )
      return

    if (user?.email) {
      posthog.identify(user.email, {
        userId: user.id,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        customerId: user.customer?.id,
        partnerId: user.customer?.partner?.id,
        role: user.role,
        type: 'customer-user'
      })
    } else if (isLoggedAsGuest === 'true') {
      actionTracking({
        action: 'identify guest',
        properties: {
          customerId: invoice?.customer?.id || 'N/A',
          partnerId: invoice?.partner?.id || 'N/A',
          partnerIDResolved: partnerId || 'N/A'
        }
      })

      posthog.identify('guest', {
        name: 'Guest',
        role: 'guest',
        customerId: invoice?.customer?.id,
        partnerId: invoice?.partner?.id
      })
    }
    if (posthog.sessionManager) {
      posthog.startSessionRecording()
    }
  }, [user, isLoggedAsGuest, invoice, posthog])

  if (!user?.email && isLoggedAsGuest === 'true') {
    return {
      user: guestUser,
      customer: null,
      loading: false,
      userType: null
    }
  }
  return {
    user,
    customer: user?.customer,
    loading,
    userType: data?.GetUserInfo?.userType
  }
}

export default useActiveUserInfo
